import {
  getRequisitionResultSets,
  LabRequisition,
  LabRequisitions,
  ModalDialog,
  Mtds,
  AttachmentsSection,
  getAttachmentsCount,
  formatSubstanceNames,
  getRequisitionStatusForLab,
  getRequisitionStatusTextForLab,
  isRequisitionContainingAnyResults,
  HashIcon,
  EmptyState,
  SubstanceTags,
  paginatedTableFeatures
} from 'prove-common'
import {
  createColumnHelper,
  flexRender,
  type PaginationState,
  type SortingState,
  useTable
} from '@tanstack/react-table'
import { DismissSampleModal } from '../../components/dismiss-sample-modal/DismissSampleModal'
import { memo, useEffect, useState } from 'react'
import { CompleteSampleModal } from '../../components/complete-sample-modal/CompleteSampleModal'

type RequisitionsTableProps = {
  data: LabRequisitions
  isFiltering?: boolean
}

const RequisitionsTable = ({
  data,
  isFiltering = false
}: RequisitionsTableProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  })
  const [isOpenViewReportsModal, setOpenViewReportsModal] = useState(false)
  const [isOpenDismissModal, setOpenDismissModal] = useState(false)
  const [isOpenCompleteModal, setOpenCompleteModal] = useState(false)
  const [selectedRequisition, setSelectedRequisition] =
    useState<LabRequisition>()

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'createdDate',
      desc: true
    }
  ])

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    )
  }, [data])

  const columnHelper = createColumnHelper<
    typeof paginatedTableFeatures,
    LabRequisition
  >()

  const columns = columnHelper.columns([
    columnHelper.accessor('sampleId', {
      header: 'ID',
      cell: (info) => (
        <Mtds.Flex data-align='center' data-gap='1'>
          <HashIcon />
          <strong>{info.getValue()}</strong>
        </Mtds.Flex>
      )
    }),
    columnHelper.accessor('substances', {
      header: 'Substances',
      sortFn: (rowA, rowB) => {
        const valueA = formatSubstanceNames(rowA.original.substances) ?? ''
        const valueB = formatSubstanceNames(rowB.original.substances) ?? ''
        return valueA.localeCompare(valueB)
      },
      cell: (info) => (
        <SubstanceTags substances={info.getValue()} showAllWhenFits={false} />
      )
    }),
    columnHelper.accessor('createdDate', {
      header: 'Created',
      cell: (info) =>
        new Date(info.getValue()).toLocaleDateString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
    }),
    // prettier-ignore
    columnHelper.accessor('status', {
      header: 'Status',
      sortFn: (rowA, rowB) => {
        const valueA = getRequisitionStatusForLab(rowA.original)
        const valueB = getRequisitionStatusForLab(rowB.original)
        return valueA.localeCompare(valueB)
      },
      cell: (info) => { // NOSONAR
        const requisition = info.row.original
        const requisitionStatus = getRequisitionStatusForLab(requisition)
        if (requisitionStatus == 'compromised') {
          const compromisedReason =
            requisition.subSamples?.length === 1
              ? requisition.subSamples[0].compromised_reason
              : undefined
          return (
            <div>
              <Mtds.Button
                popoverTarget={`requisition-status-tooltip-${requisition.id}`}
                data-popover='inline'
              >
                Dismissed
              </Mtds.Button>
              <Mtds.Popover id={`requisition-status-tooltip-${requisition.id}`}>
                {compromisedReason}
              </Mtds.Popover>
            </div>
          )
        }
        return <span>{getRequisitionStatusTextForLab(requisitionStatus)}</span>
      }
    }),
    // prettier-ignore
    columnHelper.accessor('results', {
      header: 'Uploaded reports',
      sortFn: (rowA, rowB) => {
        const attachmentsCountA = getAttachmentsCount(rowA.original) ?? []
        const attachmentsCountB = getAttachmentsCount(rowB.original) ?? []
        return attachmentsCountA - attachmentsCountB
      },
      cell: ({ row }) => {
        const attachmentsCount = getAttachmentsCount(row.original)
        return (
          <Mtds.Button
            disabled={attachmentsCount === 0}
            onClick={() => {
              setSelectedRequisition(row.original)
              setOpenViewReportsModal(true)
            }}
          >
            View ({attachmentsCount})
          </Mtds.Button>
        )
      }
    }),
    // prettier-ignore
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const status = getRequisitionStatusForLab(row.original)
        // Native result lines still use explicit completion, even without a PDF report.
        const canComplete = status === 'reports_uploaded' ||
          (status === 'no_reports_uploaded' && isRequisitionContainingAnyResults(row.original))
        const contextMenuId = `action-menu-${row.original.id}`
        return (
          <div>
            <Mtds.Button
              aria-label='Context menu'
              popoverTarget={contextMenuId}
            />
            <Mtds.Popover as='menu' id={contextMenuId}>
              <li>
                <Mtds.Button
                  onClick={() => {
                    setSelectedRequisition(row.original)
                    setOpenCompleteModal(true)
                  }}
                  disabled={!canComplete}
                >
                  Mark analysis complete
                </Mtds.Button>
              </li>

              <li>
                <Mtds.Button
                  onClick={() => {
                    setSelectedRequisition(row.original)
                    setOpenDismissModal(true)
                  }}
                  data-variant='tertiary'
                  data-color='danger'
                  disabled={row.original.status === 'Completed' || row.original.status === 'Closed' || status === 'compromised'}
                >
                  Dismiss sample
                </Mtds.Button>
              </li>
            </Mtds.Popover>
          </div>
        )
      }
    })
  ])
  const table = useTable({
    features: paginatedTableFeatures,
    data,
    columns,
    state: {
      sorting,
      pagination
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: false,
    autoResetAll: false
  })

  const resultSets =
    selectedRequisition == null
      ? undefined
      : getRequisitionResultSets(selectedRequisition)
  const attachmentsCount = selectedRequisition
    ? getAttachmentsCount(selectedRequisition)
    : undefined

  return (
    <>
      {data.length ? (
        <figure>
          <Mtds.Table data-align='center' aria-label='Samples' data-nowrap>
            <thead>
              {table.getHeaderGroups().map(({ id, headers }) => (
                <tr key={id}>
                  {headers.map((header) => (
                    <Mtds.Table.ThSortable
                      id={header.id}
                      key={header.id}
                      aria-sort={header.column.getIsSorted()}
                      onClick={
                        header.column.getCanSort()
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Mtds.Table.ThSortable>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      data-numeric={
                        cell.column.id === 'createdDate' ? true : undefined
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Mtds.Table>
        </figure>
      ) : (
        <EmptyState
          title={isFiltering ? 'No matching samples' : 'No samples'}
          description={
            isFiltering
              ? 'No samples match the selected filters.'
              : 'There are no samples to show yet.'
          }
        />
      )}
      {data.length > 0 && (
        <Mtds.Flex data-align='center' data-justify='space-between'>
          <Mtds.Pagination
            aria-label='Sidenavigering'
            current={table.state.pagination.pageIndex + 1}
            total={table.getPageCount()}
            show={table.state.pagination.pageSize}
            props={({ page }) => ({
              onClick: () => {
                window.scrollTo({ top: 0 })
                table.setPageIndex(page - 1)
              }
            })}
          />
          <p>
            Showing {table.getRowModel().rows.length} of{' '}
            {table.getPrePaginatedRowModel().rows.length}
          </p>
          <Mtds.Field
            as='select'
            value={table.state.pagination.pageSize.toString() || '20'}
            onChange={({ target }) => table.setPageSize(Number(target.value))}
            options={[
              { value: '10', label: 'Show 10 per page' },
              { value: '20', label: 'Show 20 per page' },
              { value: '50', label: 'Show 50 per page' },
              { value: '100', label: 'Show 100 per page' },
              { value: '200', label: 'Show 200 per page' }
            ]}
          />
        </Mtds.Flex>
      )}
      {selectedRequisition != null && (
        <ModalDialog
          id='view-reports-modal'
          closedby='any'
          open={isOpenViewReportsModal}
          onClose={() => setOpenViewReportsModal(false)}
        >
          <Mtds.Button
            command='close'
            commandfor={'view-reports-modal'}
            aria-label={'Close'}
          />
          <Mtds.Grid>
            <Mtds.Heading>Uploaded reports</Mtds.Heading>
            {attachmentsCount === 0 ? (
              <p>No reports have been uploaded for this sample</p>
            ) : (
              resultSets?.map((resultSet) => {
                if (resultSet == null) {
                  return null
                }
                const attachments = resultSet.attachments || []
                const hasAttachments = attachments.length > 0
                if (hasAttachments) {
                  return (
                    <AttachmentsSection
                      key={resultSet.resultId}
                      resultSet={resultSet}
                    />
                  )
                }
              })
            )}
          </Mtds.Grid>
        </ModalDialog>
      )}
      {selectedRequisition != null && (
        <DismissSampleModal
          isOpen={isOpenDismissModal}
          setOpen={setOpenDismissModal}
          requisition={selectedRequisition}
        />
      )}
      {selectedRequisition != null && (
        <CompleteSampleModal
          isOpen={isOpenCompleteModal}
          setOpen={setOpenCompleteModal}
          requisition={selectedRequisition}
        />
      )}
    </>
  )
}

export default memo(RequisitionsTable)
